import { motion } from "framer-motion";
import { ArrowDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { staggerContainer, fadeIn, slideIn } from "@/lib/animations";
import ReactTypingEffect from 'react-typing-effect';
import { useSmoothScroll } from "@/hooks/use-smooth-scroll";

export function HeroSection() {
  const { handleLinkClick } = useSmoothScroll({
    offset: 80, // Offset for fixed navbar
    duration: 1000 // Slower, smoother animation
  });
  
  return (
    <section className="relative min-h-screen flex items-center overflow-hidden bg-transparent pt-16">
      {/* No background here - using the global background from App.tsx */}
      
      <motion.div 
        variants={staggerContainer}
        initial="hidden"
        animate="show"
        className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 py-20"
      >
        <div className="flex flex-col items-center text-center">
          <motion.div variants={fadeIn("up", "tween", 0.2, 1)} className="space-y-8 max-w-3xl mx-auto">
            <h1 className="text-4xl sm:text-5xl md:text-6xl font-light tracking-tight">
              <div className="block text-white h-[70px] md:h-[80px]">
                <ReactTypingEffect
                  text={["Meet Vettor", "Hiring Made Better"]}
                  speed={100}
                  eraseSpeed={100}
                  eraseDelay={2000}
                  typingDelay={1000}
                  className="text-white font-light"
                />
              </div>
              <span className="block bg-gradient-to-r from-violet-400 to-purple-600 bg-clip-text text-transparent font-light">
                The Premium AI Agent Workflow for Recruiting
              </span>
            </h1>
            <p className="text-xl text-gray-300 mx-auto">
              Transform your recruiting process with AI-powered workflows. Save time on administrative tasks and focus on building meaningful candidate relationships.
            </p>
            <div className="flex flex-col sm:flex-row space-y-4 sm:space-y-0 sm:space-x-4 justify-center">
              <a href="#contact" onClick={handleLinkClick}>
                <Button size="lg" className="bg-violet-600 hover:bg-violet-700 text-white border-0">
                  Request Access
                </Button>
              </a>
              <a href="#how-it-works" onClick={handleLinkClick}>
                <Button size="lg" variant="outline" className="border-violet-500/50 text-violet-300 hover:bg-violet-900/30">
                  Learn More
                </Button>
              </a>
            </div>
          </motion.div>
          
          <motion.div 
            variants={fadeIn("up", "tween", 0.4, 1)}
            className="relative mt-16"
          >
            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-br from-violet-700 to-purple-500 opacity-20 rounded-2xl transform rotate-3"></div>
              <div className="bg-black border border-gray-700/30 rounded-2xl shadow-lg overflow-hidden transform -rotate-3">
                <div className="relative">
                  <img 
                    src="https://images.unsplash.com/photo-1522202176988-66273c2fd55f?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=800&h=450&q=80" 
                    alt="Diverse team of professionals" 
                    className="w-full h-auto opacity-80 mix-blend-luminosity"
                  />
                  <div className="absolute inset-0 bg-black/40"></div>
                </div>
              </div>
            </div>
            <motion.div 
              variants={fadeIn("up", "tween", 0.7, 1)}
              className="absolute -bottom-8 right-4 bg-gray-900/80 backdrop-blur-md border border-gray-800/50 p-6 rounded-xl shadow-lg space-y-3"
            >
              <div className="flex items-center space-x-3">
                <div className="h-3 w-3 bg-green-500 rounded-full"></div>
                <span className="text-sm font-medium text-gray-200">70% time saved on recruiting tasks</span>
              </div>
              <div className="flex items-center space-x-3">
                <div className="h-3 w-3 bg-violet-500 rounded-full"></div>
                <span className="text-sm font-medium text-gray-200">60% faster hiring process</span>
              </div>
            </motion.div>
          </motion.div>
        </div>
        
        <motion.div 
          variants={fadeIn("up", "tween", 0.9, 1)}
          className="absolute bottom-10 left-1/2 transform -translate-x-1/2 animate-bounce"
        >
          <a href="#how-it-works" onClick={handleLinkClick} className="text-violet-400 hover:text-violet-300">
            <ArrowDown className="w-10 h-10" />
          </a>
        </motion.div>
      </motion.div>
    </section>
  );
}
